import { ApiError, apiClient } from "./client";
import type { ValueKind } from "./types";
import type { ChartSpec } from "../components/charts/chartTypes";

// VIPER conversational, session, assist, and knowledge-index API client
// (src/api/SummitOperations.Contracts/Viper/ViperDtos.cs,
//  src/api/SummitOperations.Api/Controllers/{Viper,KnowledgeIndex}Controller.cs).

/** Persona ("hat") values accepted by ViperAskRequest.Persona (ViperPersonas). */
export type ViperPersona = "manager" | "planner" | "shopfloor" | "general";

export type ViperAnswerTier = "summit" | "reference" | "general" | string;

export interface ViperAskRequest {
  question: string;
  sessionId?: string | null;
  persona?: ViperPersona | string | null;
  followups?: boolean;
}

export interface ViperSection {
  tier: ViperAnswerTier;
  title: string;
  note: string | null;
  markdown: string;
}

export interface ViperCitation {
  number: number;
  kind: string;
  tier: string;
  title: string;
  headingPath: string;
  documentNumber: string | null;
  revision: string | null;
  url: string;
  contentId: string;
  cited: boolean;
  similarity: number | null;
  excerpt: string;
}

export interface ViperTransparencyStep {
  key: string;
  label: string;
  detail: string;
  status: "done" | "skipped" | "refused" | "warning" | string;
}

export interface ViperAnswerResponse {
  sessionId: string;
  turnId: number;
  question: string;
  outcome: string;
  label: string;
  answerMarkdown: string;
  sections: ViperSection[];
  citations: ViperCitation[];
  steps: ViperTransparencyStep[];
  valueKind: ValueKind;
  auditId: string | null;
  modelCalled: boolean;
  cacheHit: boolean;
  persona: ViperPersona | string;
  promptId: string | null;
  promptVersion: string | null;
  retrievalMode: string;
  notice: string | null;
  followups: string[];
  answeredAtUtc: string;
  /** Server-built charts from the same look-ups as the cited sources (O-R46); never written by the model. */
  charts?: ChartSpec[] | null;
}

export interface ViperSessionSummary {
  sessionId: string;
  title: string;
  persona: ViperPersona | string;
  createdAtUtc: string;
  updatedAtUtc: string;
  turns: number;
}

export interface ViperSessionDetail {
  sessionId: string;
  title: string;
  persona: ViperPersona | string;
  createdAtUtc: string;
  updatedAtUtc: string;
  turns: ViperAnswerResponse[];
}

export interface ViperAssistResponse {
  aiEnabled: boolean;
  provider: string;
  chatModel: string | null;
  embedModel: string | null;
  retrievalMode: "hybrid" | "keyword" | string;
  reason: string;
  indexReady: boolean;
  indexedSections: number;
  label: string;
  personas: string[];
  defaultPersona: string;
}

export interface ViperRelevanceFloors {
  enabled: boolean;
  admit: number;
  generalLead: number;
  floorsModel: string;
  activeModel: string;
}

export interface KnowledgeIndexRun {
  runId: string;
  kind: string;
  trigger: string;
  status: string;
  requestedBy: string;
  startedAtUtc: string;
  finishedAtUtc: string | null;
  documents: number;
  chunks: number;
  embedded: number;
  reused: number;
  removed: number;
  provider: string | null;
  embedModel: string | null;
  notes: string | null;
}

export interface KnowledgeIndexStatus {
  provider: string;
  activeEmbedModel: string | null;
  running: boolean;
  documents: number;
  sections: number;
  sectionsWithActiveVectors: number;
  sectionsWithOtherModel: number;
  modelMismatch: boolean;
  sectionsByKind: Record<string, number>;
  floors: ViperRelevanceFloors;
  runs: KnowledgeIndexRun[];
  valueKind: ValueKind;
  // Release 1.4.0 parity with Summit Engineering (O-R51): embedding status, sources by kind, stale detection.
  aiEnabled?: boolean;
  embeddingDimensions?: number | null;
  autoRefresh?: string;
  lastRun?: KnowledgeIndexRun | null;
  keywordOnlySections?: number;
  staleDocuments?: number;
  kinds?: KnowledgeIndexKind[];
  warnings?: string[];
}

/** One source kind on the Knowledge index page (release 1.4.0). */
export interface KnowledgeIndexKind {
  kind: string;
  label: string;
  sourceConnected: boolean;
  documents: number;
  sections: number;
  embedded: number;
  keywordOnly: number;
  staleDocuments: number;
  lastIndexedAtUtc: string | null;
}

export interface KnowledgeCalibrationProbe {
  id: string;
  group: string;
  question: string;
  bestSimilarity: number | null;
  bestSource: string | null;
}

export interface KnowledgeCalibration {
  runId: string;
  provider: string;
  embedModel: string | null;
  onTopicMin: number | null;
  onTopicMedian: number | null;
  generalMax: number | null;
  offTopicMax: number | null;
  suggestedAdmit: number | null;
  suggestedGeneralLead: number | null;
  separable: boolean;
  note: string;
  probes: KnowledgeCalibrationProbe[];
  valueKind: ValueKind;
}

/** The latest calibration, or none yet (the endpoint always answers 200). */
export interface KnowledgeCalibrationLatest {
  exists: boolean;
  calibration: KnowledgeCalibration | null;
  message: string;
}

export const DEFAULT_AI_OFF_REASON =
  "AI is off in this environment. VIPER still refuses, redirects and lists matching sections, written without AI.";

export const DEFAULT_STARTER_PROMPTS: readonly string[] = [
  "How do we verify first-article readiness before releasing a CNC operation?",
  "What is the difference between critical ratio and least slack on a bottleneck workcenter?",
  "How does Line of Balance show which sub-assembly holds up a top-level delivery?",
  "What does our tool-offset verification procedure require after a fixture change?"
];

export const VIPER_QK = {
  assist: ["viper", "assist"] as const,
  sessions: ["viper", "sessions"] as const,
  session: (id: string | null) => ["viper", "session", id ?? "none"] as const,
  knowledgeIndex: ["admin", "knowledge-index"] as const,
  latestCalibration: ["admin", "knowledge-index", "calibration", "latest"] as const
};

export function isViperUnavailableError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 503 || error.status === 0);
}

export const viperApi = {
  /** GET /api/v1/viper/assist — whether VIPER can write answers right now, and why not when it cannot. */
  getAssist: async (): Promise<ViperAssistResponse> => {
    try {
      return (await apiClient.get<ViperAssistResponse>("/viper/assist")).data;
    } catch (error) {
      if (isViperUnavailableError(error)) {
        return {
          aiEnabled: false,
          provider: "Disabled",
          chatModel: null,
          embedModel: null,
          retrievalMode: "keyword",
          reason: DEFAULT_AI_OFF_REASON,
          indexReady: false,
          indexedSections: 0,
          label: "VIPER · written without AI",
          personas: ["planner", "manager", "shopfloor", "general"],
          defaultPersona: "planner"
        };
      }
      throw error;
    }
  },

  /** POST /api/v1/viper/ask — asks VIPER a question in the given session and persona. */
  ask: async (request: ViperAskRequest): Promise<ViperAnswerResponse> =>
    (
      await apiClient.post<ViperAnswerResponse>("/viper/ask", {
        question: request.question,
        sessionId: request.sessionId ?? null,
        persona: request.persona ?? null,
        followups: request.followups ?? true
      })
    ).data,

  /** GET /api/v1/viper/sessions — lists the signed-in person's private server-side conversations. */
  listSessions: async (): Promise<ViperSessionSummary[]> =>
    (await apiClient.get<ViperSessionSummary[]>("/viper/sessions")).data,

  /** GET /api/v1/viper/sessions/{id} — loads one conversation with all of its turns. */
  getSession: async (sessionId: string): Promise<ViperSessionDetail> =>
    (await apiClient.get<ViperSessionDetail>(`/viper/sessions/${encodeURIComponent(sessionId)}`)).data,

  /** DELETE /api/v1/viper/sessions/{id} — deletes one private conversation (audit log rows remain untouched). */
  deleteSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/viper/sessions/${encodeURIComponent(sessionId)}`);
  },

  /** GET /api/v1/admin/knowledge-index — status, counts, active embedding model, relevance floors, and recent runs. */
  getKnowledgeIndexStatus: async (): Promise<KnowledgeIndexStatus> =>
    (await apiClient.get<KnowledgeIndexStatus>("/admin/knowledge-index")).data,

  /** POST /api/v1/admin/knowledge-index/rebuild — triggers a full rebuild (202 Accepted, or 409 Conflict if running). */
  rebuildKnowledgeIndex: async (reembed = false): Promise<KnowledgeIndexRun> =>
    (await apiClient.post<KnowledgeIndexRun>(`/admin/knowledge-index/rebuild${reembed ? "?reembed=true" : ""}`, {})).data,

  /** POST /api/v1/admin/knowledge-index/calibrate — runs relevance-floor calibration against the active embedding model. */
  calibrateKnowledgeIndex: async (): Promise<KnowledgeCalibration> =>
    (await apiClient.post<KnowledgeCalibration>("/admin/knowledge-index/calibrate", {})).data,

  /**
   * GET /api/v1/admin/knowledge-index/calibrations/latest — always 200: { exists, calibration, message }.
   * Returns the most recent calibration run, or null when none has been run yet.
   */
  getLatestCalibration: async (): Promise<KnowledgeCalibration | null> =>
    (await apiClient.get<KnowledgeCalibrationLatest>("/admin/knowledge-index/calibrations/latest")).data?.calibration ?? null
};

